import React, { Fragment, useEffect } from 'react';
import { Input, Loader } from 'freemium-ui';
import { useDropzone } from 'react-dropzone';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import styled from 'styled-components';
import Icon from '../../../../../../shared/components/Icon';
import Spacer from '../../../../../../shared/components/Spacer';
import { ErrorField } from '../../../../../../shared/styles';
import { validURL } from '../../../../../../shared/utils';
import request from '../../../../../../utils/request';

type UploadLogoProps = {
  form: UseFormReturn;
  label?: string;
  isRequired?: boolean;
  placeholder?: string;
  tipText?: string;
  recommendedSize?: string;
  logoType: string;
  surveyId?: string | null;
  fieldId?: string;
  id?: string;
};

const UploadLogo = (props: UploadLogoProps) => {
  const {
    form,
    fieldId = '',
    label,
    recommendedSize = '150 x 75',
    placeholder,
    logoType,
    isRequired = false,
  } = props;
  const { t } = useTranslation();
  const [isWebLink, setIsWebLink] = React.useState(false);
  const [logo, setLogo] = React.useState<any>(null);

  const {
    watch,
    setValue,
    formState: { errors },
    register,
  } = form;

  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const {
    mutate: upload,
    isLoading: isUploading,
    isError: uploadError,
  } = useUploadLogo({
    callback: (isSuccess: any, response: any) => {
      if (isSuccess) {
        setLogo(response);
        setFormValue(fieldId, response);
      }
    },
  });

  const onDrop = (files: any) => {
    if (files?.length) {
      const formdata: any = new FormData();
      formdata.append('file', files[0], files[0].name);
      upload({ payload: formdata, logoType });
    }
  };

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    fileRejections,
  } = useDropzone({
    accept: 'image/png, image/jpg, image/jpeg',
    onDrop,
    maxFiles: 1,
    maxSize: 1000000,
    disabled: Boolean(logo) || isUploading,
  });

  const handleRemove = () => {
    setLogo(null);
    setFormValue(fieldId, '');
  };

  useEffect(() => {
    if (fieldId && watch(fieldId)) {
      setIsWebLink(true);
      setLogo(watch(fieldId));
    } else {
      setLogo(null);
    }
  }, [fieldId, watch(fieldId)]);

  return (
    <Wrapper id={props.id}>
      <div className="title-cont">
        <div className="custom-label">{label}&nbsp;</div>
        <div
          id="web-link"
          className="toggle-btn"
          onClick={() => setIsWebLink(!isWebLink)}
        >
          {!watch(fieldId)?.length ? (
            <div
              className="toggle-btn"
              onClick={() => setIsWebLink(!isWebLink)}
            >
              {isWebLink
                ? `${t('settings.account_info.logo.upload_logo')}`
                : `${t('settings.account_info.logo.use_web_link')}`}
            </div>
          ) : null}
        </div>
      </div>
      {isWebLink ? (
        <>
          <Input
            placeholder={`${t('settings.account_info.logo.logo_url')}`}
            {...register(fieldId)}
            isErrored={!!errors[fieldId]}
            isRequired={isRequired}
            errorText={errors[fieldId]?.message}
            endAdornment={
              watch(fieldId) && !errors[fieldId] ? (
                <div className="link" onClick={() => handleRemove()}>
                  {t('contacts.menu.remove.text')}
                </div>
              ) : null
            }
          />
        </>
      ) : (
        <>
          {logo ? (
            <>
              <Input
                disabled
                value={logo}
                endAdornment={
                  <div className="link" onClick={() => handleRemove()}>
                    {t('contacts.menu.remove.text')}
                  </div>
                }
              />
            </>
          ) : (
            <>
              <Uploadcontainer
                {...getRootProps()}
                isFocused={isFocused}
                isDragAccept={isDragAccept}
                isDragReject={isDragReject}
              >
                {isUploading ? (
                  <Loader />
                ) : (
                  <>
                    <input {...getInputProps()} />
                    <Icon width="14" height="15" glyph="upload" fill="none" />
                    <Spacer y={0.5} />
                    <div className="link">{placeholder}</div>
                    <p className="size">
                      {t('settings.account_info.logo.max_file_size')}
                    </p>
                    <p className="support">
                      {t('settings.account_info.logo.supported_format')}
                    </p>
                    <p className="support">
                      {t('settings.account_info.logo.recommended_size', {
                        recommendedSize: recommendedSize,
                      })}
                    </p>
                  </>
                )}
              </Uploadcontainer>
              {fileRejections.map(({ file, errors }: any) => (
                <Fragment key={file.path}>
                  {errors.length ? (
                    <ErrorField>
                      {t('settings.account_info.logo.file_size')}
                    </ErrorField>
                  ) : null}
                </Fragment>
              ))}
              {uploadError && (
                <ErrorField>
                  {t('settings.account_info.logo.upload_error')}
                </ErrorField>
              )}
            </>
          )}
        </>
      )}
      {logo && validURL(logo) ? (
        <div className="preview">
          <img src={logo} alt="preview-logo" />
        </div>
      ) : null}
    </Wrapper>
  );
};

export default UploadLogo;

export const useUploadLogo = ({ callback }: { callback?: any }) => {
  return useMutation(
    async ({ payload, logoType }: any) => {
      const url =
        logoType === 'account-logo'
          ? '/surveyserv/accounts/logo'
          : '/accounts/favicon/upload';
      const data = await request(url, payload, 'POST', {
        'Content-Type': 'multipart/form-data',
      });
      return data;
    },
    {
      onSuccess: (response: any) => {
        if (callback) callback(true, response.url);
      },
      onError: (error: any) => {
        if (callback) callback(false, error);
      },
    },
  );
};

export const Uploadcontainer = styled.div<any>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border: 2px dashed #eee;
  border-radius: 5px;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
  width: 100%;
  cursor: pointer;
  margin-bottom: 20px;
  ${({ isFocused }) => isFocused && 'borderColor: #2196f3;'}
  ${({ isDragAccept }) => isDragAccept && 'borderColor: #00e676;'}
  ${({ isDragReject }) => isDragReject && 'borderColor: #ff1744;'}
  .link {
    font-weight: 500;
    font-size: 13px;
    line-height: 20px;
    text-align: center;
    color: #2c5cc5;
  }
  p.size {
    margin: 0 0 8px 0 !important;
    font-size: 13px;
  }
  p.support {
    margin: 0 !important;
    font-size: 11px;
  }
`;
export const Wrapper = styled.div`
  width: 100%;
  .title-cont {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }
  .link {
    color: var(--text-link);
    display: block;
    margin: 0;
    padding: 4px 15px 5px;
    cursor: pointer;
    line-height: 1.5;
    text-align: center;
    font-weight: 500;
    user-select: none;
    font-size: 14px;
    height: auto;
    background-color: #fbfbfb;
  }
  .toggle-btn {
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    text-decoration-line: underline;
    color: #475867;
    cursor: pointer;
  }

  .end-adornment {
    padding: 0 !important;
  }

  .preview {
    background: #ffffff;
    border: 1px solid #f2f2f2;
    box-shadow: inset 0px 1px 2px rgba(24, 50, 71, 0.05);
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
    margin-top: 10px;

    img {
      height: 80px;
    }
  }
`;
